import React from 'react';
import {
    Edit2, Check, Sparkles, Loader2, RotateCcw, AlertTriangle,
    PanelLeftClose, PanelLeftOpen, ChevronDown, Clock, RotateCcw as RevertIcon
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { formatLocalDateTime } from '../../../../utils/date';
import { Avatar } from '../../../dashboard/components/DashboardComponents';
import { SECTION_STATUS } from '../../../../shared/utils/statusConstants';

/**
 * EditorCardHeader — Redesigned with premium picture-perfect header metadata,
 * avatar initials badge, status indicators, and clean action buttons.
 */
const EditorCardHeader = ({
    sectionTitle,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    editableData,
    isEditingContent,
    isUpdating,
    handleUpdate,
    startEditing,
    cancelEditing,
    setShowAIModal,
    setShowVersionHistory,
    isNoGo = false,
    isManager = false,
    isTeamLeadViewOnly = false,
    assignments = {},
    selectedSection = {},
    // Global revert (TL-only)
    onCancelSync = null,

}) => {
    const [showCancelConfirm, setShowCancelConfirm] = React.useState(false);
    const [cancelPending, setCancelPending] = React.useState(false);
    // Resolve Assignment Metadata
    const assignedRaw = assignments[sectionTitle] ||
        assignments[selectedSection?.key] ||
        assignments[selectedSection?.label] ||
        assignments[selectedSection?.id] ||
        assignments[selectedSection?._id] ||
        selectedSection?.assigned_to || [];
    const assignedArray = Array.isArray(assignedRaw) ? assignedRaw : [assignedRaw];
    const memberNames = assignedArray.map(m => typeof m === 'object' ? (m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || 'Contributor') : String(m));
    const isAssigned = memberNames.length > 0;
    const assignedName = isAssigned ? memberNames[0] : 'Unassigned';

    // Get Avatar Initials
    const initials = isAssigned
        ? assignedName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
        : '';

    // Format updated_at time to hh:mm format
    const formatTime = (dateStr) => {
        if (!dateStr) return 'Not available';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) {
                const match = dateStr.match(/\b(\d{2}):(\d{2})\b/);
                if (match) {
                    return `${match[1]}:${match[2]}`;
                }
                return 'Not available';
            }
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${hours}:${minutes}`;
        } catch (e) {
            return 'Not available';
        }
    };
    const displayTime = formatTime(selectedSection?.updated_at);

    // Resolve Status Metadata
    const statusObj = selectedSection?.status && typeof selectedSection?.status === 'object' ? selectedSection.status : {};
    const statusLabelRaw = statusObj.label || (typeof selectedSection?.status === 'string' ? selectedSection.status : '');

    let sectionStatus = statusLabelRaw ||
        editableData?.SECTION_STATUS?.[selectedSection?.key] || editableData?.section_status?.[selectedSection?.key] ||
        editableData?.SECTION_STATUS?.[selectedSection?.label] || editableData?.section_status?.[selectedSection?.label] || '';

    const currentStatusLower = String(sectionStatus).toLowerCase();
    if (isAssigned && (currentStatusLower === '' || currentStatusLower === 'none' || currentStatusLower.includes('unassigned'))) {
        sectionStatus = 'Section Assigned';
    } else if (currentStatusLower === '' || currentStatusLower === 'none' || currentStatusLower.includes('unassigned')) {
        sectionStatus = 'Unassigned';
    }

    const statusCode = statusObj.code || selectedSection?.status_code || parseInt(sectionStatus) || 0;

    // Resolve display label from central status definitions
    let displayStatus = SECTION_STATUS[statusCode]?.label || (() => {
        const lower = String(sectionStatus).toLowerCase();
        if (lower.includes('unassigned')) return 'Section Unassigned';
        if (lower.includes('session expired') || lower.includes('session-expired')) return 'Section Unassigned';
        if (lower.includes('auto-closed') || lower.includes('autoclosed') || lower.includes('auto closed')) return 'Auto-Closed';
        if (lower.includes('revision') || lower.includes('rejected')) return 'Section Revision Needed';
        if (lower.includes('submitted') || lower.includes('review')) return 'Section Submitted for Review';
        if (lower.includes('approved')) return 'Section TL Approved';
        if (lower.includes('progress')) return 'Section In-Progress';
        if (lower.includes('assigned') || isAssigned) return 'Section Assigned';
        return sectionStatus || 'Section Unassigned';
    })();

    const isAutoClosed = statusCode === 57 || displayStatus === 'Auto-Closed';
    const isSessionExpired = statusCode === 56 || displayStatus === 'Session Expired';

    const statusLabel = String(
        editableData?.global_status?.label ||
        editableData?.global_status?.name ||
        editableData?.proposal_stage ||
        editableData?.PROPOSAL_STAGE ||
        editableData?.status ||
        ''
    ).toLowerCase();

    const statusCodeVal = Number(
        editableData?.global_status?.code ||
        editableData?.status_code ||
        editableData?.global_status_code ||
        editableData?.proposal_stage_code ||
        0
    );

    const isExpired = statusCodeVal === 56 || statusLabel.includes('expired') || isSessionExpired;

    // Map to proper CSS status color class
    let statusClass = 'other';
    const statusStr = String(sectionStatus).toLowerCase();
    const isApproved = statusStr.includes('approved') || statusStr.includes('finalised') || displayStatus === 'Section TL Approved';
    if (isAutoClosed) {
        statusClass = 'auto-closed';
    } else if (isSessionExpired) {
        statusClass = 'session-expired';
    } else if (isApproved) {
        statusClass = 'approved';
    } else if (statusStr.includes('revision') || statusStr.includes('rejected') || displayStatus === 'Section Revision Needed') {
        statusClass = 'revision';
    } else if (statusStr.includes('submitted') || statusStr.includes('awaiting review') || displayStatus === 'Section Submitted for Review') {
        statusClass = 'submitted';
    } else if (statusStr.includes('progress') || statusStr.includes('draft') || displayStatus === 'Section In-Progress') {
        statusClass = 'in-progress';
    } else if (statusStr.includes('unassigned') || displayStatus === 'Section Unassigned') {
        statusClass = 'unassigned';
    } else if (statusStr.includes('assigned') || displayStatus === 'Section Assigned') {
        statusClass = 'assigned';
    }

    const isLockedForMember = !isManager && (statusClass === 'submitted' || isAutoClosed || isSessionExpired);

    // Format updated_at to dd/mm/yyyy
    const formatUpdatedDate = (dateStr) => {
        return formatLocalDateTime(dateStr);
    };
    const updatedDate = formatUpdatedDate(selectedSection?.updated_at || editableData?.updated_at);

    return (
        <>
            <div className="final-card-header" style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'stretch', justifyContent: 'flex-start', flexWrap: 'nowrap', minWidth: '0' }}>
                {/* Top Row: Title Group on Left, Action Buttons on Right */}
                <div className="header-top-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', minWidth: '0' }}>
                    <div className="card-title-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '0' }}>
                        <button
                            className="sidebar-toggle-trigger"
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            title={isSidebarCollapsed ? "Open section sidebar" : "Close section sidebar"}
                            style={{
                                background: 'transparent', border: 'none', color: '#64748B',
                                cursor: 'pointer', padding: '4px', borderRadius: '4px',
                                display: 'flex', alignItems: 'center', transition: 'all 0.2s',
                                marginLeft: '-4px', flexShrink: 0
                            }}
                        >
                            {isSidebarCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
                        </button>
                        <h4 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, letterSpacing: '0.01em', color: '#0F172A', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sectionTitle}</h4>
                    </div>

                    <div className="header-action-btns" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                        {!isNoGo && !isApproved && !isLockedForMember && (editableData?.global_status?.code === undefined ||
                            [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 56].includes(Number(editableData.global_status.code))) && (
                                <>
                                    <button
                                        className="final-ai-btn header-ai-btn"
                                        onClick={() => !isTeamLeadViewOnly && !isExpired && setShowAIModal(true)}
                                        disabled={isTeamLeadViewOnly || isExpired}
                                        style={{
                                            background: '#FF5526', color: '#FFFFFF', border: 'none',
                                            display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem',
                                            fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.2s',
                                            boxShadow: '0 1px 3px rgba(240, 74, 35, 0.15)', whiteSpace: 'nowrap',
                                            opacity: (isTeamLeadViewOnly || isExpired) ? 0.5 : 1,
                                            cursor: (isTeamLeadViewOnly || isExpired) ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        <Sparkles size={13} style={{ flexShrink: 0 }} />
                                        <span style={{ whiteSpace: 'nowrap' }}>AI Assist</span>
                                        <ChevronDown size={13} style={{ opacity: 0.8, flexShrink: 0 }} />
                                    </button>

                                    <button
                                        className={`final-edit-btn save-changes-btn ${isEditingContent ? 'update-mode' : ''}`}
                                        onClick={() => { if (!isTeamLeadViewOnly && !isExpired) { if (isEditingContent) { handleUpdate(); } else { startEditing(); } } }}
                                        disabled={isUpdating || isTeamLeadViewOnly || isExpired}
                                        style={{
                                            background: isEditingContent ? '#FF5526' : '#F1F5F9',
                                            color: isEditingContent ? '#FFFFFF' : '#1E293B',
                                            border: isEditingContent ? 'none' : '1px solid #E2E8F0',
                                            display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem',
                                            fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.2s',
                                            whiteSpace: 'nowrap',
                                            opacity: (isTeamLeadViewOnly || isExpired) ? 0.5 : 1,
                                            cursor: (isUpdating || isTeamLeadViewOnly || isExpired) ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {isUpdating
                                            ? <Loader2 className="animate-spin" size={14} style={{ flexShrink: 0 }} />
                                            : (isEditingContent ? null : <Edit2 size={13} style={{ flexShrink: 0 }} />)}
                                        <span style={{ whiteSpace: 'nowrap' }}>{isUpdating ? 'Updating...' : (isEditingContent ? 'Save Changes' : 'Edit')}</span>
                                    </button>

                                    {isEditingContent && (
                                        <button
                                            className="final-edit-btn cancel-btn"
                                            style={{
                                                background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0',
                                                display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem',
                                                fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.2s',
                                                whiteSpace: 'nowrap',
                                                opacity: (isTeamLeadViewOnly || isExpired) ? 0.5 : 1,
                                                cursor: (isTeamLeadViewOnly || isExpired) ? 'not-allowed' : 'pointer'
                                            }}
                                            onClick={() => setShowCancelConfirm(true)}
                                            disabled={isTeamLeadViewOnly || isExpired}
                                        >
                                            <span style={{ whiteSpace: 'nowrap' }}>Cancel</span>
                                        </button>
                                    )}

                                </>
                            )}
                        <button
                            className="final-edit-btn version-history-btn"
                            style={{
                                background: '#FFFFFF', color: '#1E293B', border: '1px solid #E2E8F0',
                                display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.45rem 0.85rem',
                                fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                            }}
                            onClick={() => setShowVersionHistory(true)}
                        >
                            <RotateCcw size={13} style={{ flexShrink: 0 }} />
                            <span style={{ whiteSpace: 'nowrap' }}>Version History</span>
                        </button>
                    </div>
                </div>

                {/* Bottom Row: Picture Perfect Detailed Subtitle Metadata Row - Only for Managers */}
                {isManager && (
                    <div className="editor-subtitle-metadata" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.75rem', color: '#64748B', fontWeight: 500, flexWrap: 'wrap', overflow: 'hidden', marginLeft: '34px', marginTop: '0.1rem' }}>
                        <span className="metadata-item" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <span style={{ whiteSpace: 'nowrap' }}>Assigned to</span>
                            {isAssigned ? (
                                <div className="avatar-stack" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {assignedArray.slice(0, 2).map((member, idx) => {
                                        const mName = typeof member === 'object' ? (member.name || `${member.first_name || ''} ${member.last_name || ''}`) : String(member);
                                        return (
                                            <span
                                                key={idx}
                                                className="assigned-user-pill"
                                                title={mName}
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', whiteSpace: 'nowrap' }}
                                            >
                                                <Avatar name={mName} size={18} />
                                                {assignedArray.length <= 2 && (
                                                    <span className="user-name" style={{ color: '#1E293B', fontWeight: 600, whiteSpace: 'nowrap' }}>{mName}</span>
                                                )}
                                            </span>
                                        );
                                    })}
                                    {assignedArray.length > 2 && (
                                        <span
                                            className="avatar-circle plus-more"
                                            title={`Assigned Members:\n${memberNames.map((name, i) => `${i + 1}. ${name}`).join('\n')}`}
                                            style={{
                                                width: '18px', height: '18px', borderRadius: '50%', background: '#CBD5E1',
                                                color: '#475569', fontSize: '0.6rem', fontWeight: 700, display: 'flex',
                                                alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer'
                                            }}
                                        >
                                            +{assignedArray.length - 2}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <span className="unassigned-text" style={{ color: '#94A3B8', fontStyle: 'italic', whiteSpace: 'nowrap' }}>None</span>
                            )}
                        </span>

                        <span className="metadata-separator" style={{ color: '#CBD5E1', flexShrink: 0 }}>|</span>

                        <span className="metadata-item" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <span style={{ whiteSpace: 'nowrap' }}>Status</span>
                            <span className={`section-approved-badge ${statusClass}`} style={{
                                fontSize: '0.7rem', padding: '2px 8px', borderRadius: '6px', fontWeight: 600, whiteSpace: 'nowrap'
                            }}>
                                {displayStatus}
                            </span>
                        </span>
                    </div>
                )}
            </div>

            {/* ── Cancel Confirmation Modal ───────────────────────────────────────── */}
            {showCancelConfirm && createPortal(
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="revert-modal-title"
                    style={{
                        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                        backgroundColor: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(6px)',
                        zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '24px'
                    }}
                    onClick={e => { if (e.target === e.currentTarget) setShowCancelConfirm(false); }}
                >
                    <div style={{
                        background: '#FFFFFF', borderRadius: '20px',
                        boxShadow: '0 24px 64px rgba(15,23,42,0.22)',
                        padding: '0', width: '100%', maxWidth: '480px',
                        overflow: 'hidden',
                        animation: 'revert-modal-in 0.22s cubic-bezier(0.16,1,0.3,1)',
                    }}>
                        {/* Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #FEF2F2 0%, #FFF5F5 100%)',
                            borderBottom: '1px solid #FECACA',
                            padding: '24px 28px 20px',
                            display: 'flex', alignItems: 'flex-start', gap: '14px'
                        }}>
                            <div style={{
                                width: '40px', height: '40px', borderRadius: '12px',
                                background: '#FEE2E2', border: '1px solid #FECACA',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}>
                                <AlertTriangle size={20} color="#DC2626" />
                            </div>
                            <div>
                                <h3 id="revert-modal-title" style={{
                                    margin: 0, fontSize: '17px', fontWeight: 700,
                                    color: '#0F172A', letterSpacing: '-0.01em'
                                }}>Cancel and Sync Section?</h3>
                                <p style={{
                                    margin: '4px 0 0', fontSize: '12.5px',
                                    color: '#64748B', fontWeight: 400, lineHeight: 1.5
                                }}>This action will affect all collaborators on this section.</p>
                            </div>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '22px 28px' }}>
                            <p style={{
                                margin: 0, fontSize: '14px', color: '#334155',
                                lineHeight: 1.65, fontWeight: 400
                            }}>
                                This will <strong>discard all saved collaborative edits</strong> and restore
                                the original content for <strong>everyone assigned to this section</strong>.
                            </p>
                            <div style={{
                                marginTop: '14px',
                                background: '#FFF7ED', border: '1px solid #FED7AA',
                                borderRadius: '10px', padding: '12px 16px',
                                display: 'flex', gap: '10px', alignItems: 'flex-start'
                            }}>
                                <AlertTriangle size={14} color="#D97706" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <p style={{ margin: 0, fontSize: '12.5px', color: '#92400E', lineHeight: 1.5 }}>
                                    All team members will immediately see the reverted content.
                                    This cannot be undone without saving new changes.
                                </p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div style={{
                            padding: '0 28px 24px',
                            display: 'flex', justifyContent: 'flex-end', gap: '10px'
                        }}>
                            <button
                                id="btn-revert-keep-editing"
                                onClick={() => setShowCancelConfirm(false)}
                                disabled={cancelPending}
                                style={{
                                    background: '#F1F5F9', color: '#475569',
                                    border: '1px solid #E2E8F0',
                                    padding: '10px 20px', borderRadius: '10px',
                                    fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
                                    transition: 'all 0.18s'
                                }}
                            >
                                Keep Editing
                            </button>
                            <button
                                id="btn-revert-confirm"
                                disabled={cancelPending}
                                onClick={async () => {
                                    setCancelPending(true);
                                    try {

                                        if (onCancelSync) { await onCancelSync(); } else { cancelEditing(); }
                                    } finally {
                                        setCancelPending(false);
                                        setShowCancelConfirm(false);
                                    }
                                }}
                                style={{
                                    background: cancelPending ? '#F87171' : '#DC2626',
                                    color: '#FFFFFF', border: 'none',
                                    padding: '10px 22px', borderRadius: '10px',
                                    fontSize: '13.5px', fontWeight: 700, cursor: cancelPending ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    transition: 'all 0.18s',
                                    boxShadow: '0 4px 14px rgba(220,38,38,0.25)'
                                }}
                                onMouseEnter={e => { if (!cancelPending) e.currentTarget.style.background = '#B91C1C'; }}
                                onMouseLeave={e => { if (!cancelPending) e.currentTarget.style.background = '#DC2626'; }}
                            >
                                {cancelPending && <Loader2 size={13} className="animate-spin" />}
                                <span>{cancelPending ? 'Syncing...' : 'Cancel & Sync'}</span>
                            </button>
                        </div>
                    </div>

                    <style>{`
                    @keyframes revert-modal-in {
                        from { opacity: 0; transform: scale(0.93) translateY(12px); }
                        to   { opacity: 1; transform: scale(1)   translateY(0); }
                    }
                `}</style>
                </div>,
                document.body
            )}
        </>
    );
};

export default EditorCardHeader;
